//adapted from https://github.com/mblarsen/vue-browser-acl

import type { App, DirectiveBinding, ObjectDirective } from "vue";
import type { Router, RouteMeta } from "vue-router";
import Acl from "./acl";
import type { AclUser, AclTenantRef } from "./acl";

export const acl = new Acl();
(window as Record<string, unknown>).acl = acl;

export interface CanFunction {
  (permission: string, obj?: unknown): boolean
  not: (permission: string, obj?: unknown) => boolean
}

export interface AclPluginOptions {
  directive?: string
  failRoute?: string
  router?: Router
  userAccessor: () => AclUser | null
  tenantAccessor: () => AclTenantRef | null
  tokenAccessor?: () => string | null
}

export default {
  install(app: App, rawOptions: Partial<AclPluginOptions> = {}) {
    const currentUser = rawOptions.userAccessor!;
    const currentTenant = rawOptions.tenantAccessor!;
    const currentToken = (): string | null =>
      rawOptions.tokenAccessor ? rawOptions.tokenAccessor() : null;

    const options: AclPluginOptions = {
      directive: "can",
      failRoute: "/",
      ...rawOptions,
    };

    (acl as Record<string, unknown>).router = (router: Router) => {
      options.router = router;

      const canNavigate = (resource: string | null, action: string | null): boolean => {
        if (resource && action) {
          return acl.can(currentUser(), currentTenant(), resource, action);
        }
        return false;
      };
      /* convert 'account:create'  to ['account', 'create'] */
      const metaToStatementPair = (meta: RouteMeta): [string | null, string | null] => {
        const [resource = null, action = null] = ((meta.can as string) || "").split(":");
        return [resource, action];
      };

      interface ChainPromise extends Promise<boolean> {
        getFail: () => string | null | undefined
      }

      const chainCans = (metas: RouteMeta[]): ChainPromise => {
        let fail: string | null | undefined = null;
        const chain = metas.reduce((chain: Promise<boolean>, meta: RouteMeta) => {
          return chain
            .then((can: boolean) => {
              if (!can) return can;
              fail = meta.fail as string | null | undefined;
              return Promise.resolve(canNavigate(...metaToStatementPair(meta)));
            })
            .catch((error: Error) => {
              console.error(error);
              return false;
            });
        }, Promise.resolve(true)) as ChainPromise;
        chain.getFail = () => fail;
        return chain;
      };

      router.beforeEach((to, from, next) => {
        const metas = to.matched
          .filter((route) => route.meta && route.meta.can)
          .map((route) => route.meta);

        const chain = chainCans(metas);
        chain.then((result) => {
          if (result === true) {
            return next();
          }
          const fail =
            chain.getFail() === "$from" ? from.path : chain.getFail();
          next(fail || options.failRoute);
        });
      });
    };

    /* init router */
    if (options.router) {
      (acl as Record<string, unknown> & { router: (r: Router) => void }).router(options.router);
    }

    /* create directive */
    const directive: ObjectDirective<HTMLElement> = (el: HTMLElement, binding: DirectiveBinding) => {
      const behaviour = binding.modifiers.disable ? "disable" : "hide";
      const [action, resource] = stringToExprTpl(binding);

      if (!action || !resource) {
        throw new Error(
          `v-can: invalid permission "${binding.value}". Expected "resource:action" format.`
        );
      }

      const aclMethod: "can" =
        (binding.modifiers.some && ("some" as "can")) ||
        (binding.modifiers.every && ("every" as "can")) ||
        "can";

      const ok = acl[aclMethod](
        currentUser(),
        currentTenant(),
        resource,
        action
      );
      const not = binding.modifiers.not;

      if ((ok && not) || (!ok && !not)) {
        if (behaviour === "hide") {
          commentNode(el);
        } else if (behaviour === "disable") {
          (el as HTMLButtonElement).disabled = true;
        } else if (behaviour === "readonly") {
          (el as HTMLInputElement).readOnly = true;
        }
      }
    };

    app.directive(options.directive!, directive);

    /* $can helper */
    const can: CanFunction = (permission: string, obj?: unknown): boolean => {
      const [resource, action] = permission.split(":");
      return acl.can(
        currentUser(),
        currentTenant(),
        resource,
        action,
      );
    };
    can.not = (permission: string, _obj?: unknown): boolean => {
      const [resource, action] = permission.split(":");
      return !acl.can(
        currentUser(),
        currentTenant(),
        resource,
        action,
      );
    };

    const helper = `$${options.directive}`;
    app.config.globalProperties[helper] = can;

    // Add scope checking helper
    app.config.globalProperties.$hasScope = (requiredScope: string): boolean => {
      const token = currentToken();
      return token ? acl.hasScope(token, requiredScope) : false;
    };

    // Provide $can for inject() in Composition API
    app.provide('$can', can);
  },
};

/**
 * Create comment node
 *
 * @private
 * @author https://stackoverflow.com/questions/43003976/a-custom-directive-similar-to-v-if-in-vuejs#43543814
 */
function commentNode(el: HTMLElement): void {
  const comment = document.createComment(" ");

  Object.defineProperty(comment, "setAttribute", {
    value: () => undefined,
  });

  if (el.parentNode) {
    el.parentNode.replaceChild(comment, el);
  }
}

/**
 * Maps binding.value of type string to expression tuple
 */
const stringToExprTpl = ({ arg, value }: DirectiveBinding): [string | undefined, string | undefined, unknown[]] => {
  let subject: string | undefined;
  let verb: string | undefined;

  if (arg) {
    subject = value;
    verb = arg;
  } else {
    [subject, verb] = (value as string).split(":");
  }

  return [verb, subject, []];
};
